import { Alert, downloadFile, getNCFileFromVersions, injectConditionalArrayItems } from '@helpers';
import { checkEvent } from '@reducers/utils';
import { AlertAction } from '@components/alert';
import { NCFileSearchItem } from '@components/common/NCFileType/NCFileWizardNoERP/types';
import {
  GetNCFileHashAndTransferParams,
  GetNcFileHashAndTransferWithAPICallParams,
  HandleNCFileUploadingProcessParams,
  ShareNCFileParams,
} from '../types';
import i18n from 'i18n-js';

export const getNcFileHashAndTransferWithAPICall = async ({ getNCFileById, ncFileId }: GetNcFileHashAndTransferWithAPICallParams) => {
  let ncFileContentHash;
  let ncFileTransferFromMachineState;

  const ncFile = await getNCFileById(ncFileId);
  const version = getNCFileFromVersions(ncFile?.versions);
  if (version?.fileContentHash) {
    ncFileContentHash = version?.fileContentHash;
  }
  if (version?.ncFileTransferFromMachineState) {
    ncFileTransferFromMachineState = version?.ncFileTransferFromMachineState;
  }

  return { ncFileContentHash, ncFileTransferFromMachineState };
};

export const getNCFileHashAndTransfer = async ({
  selectedItem,
  getNCFileById,
  ncFileId: _ncFileId,
  selectedVersion,
}: GetNCFileHashAndTransferParams) => {
  if (selectedVersion) {
    return {
      ncFileContentHash: selectedVersion.fileContentHash,
      ncFileTransferFromMachineState: selectedVersion.ncFileTransferFromMachineState,
    };
  }

  if (_ncFileId && _ncFileId !== selectedItem?.machineStatus?.ncFileId) {
    return getNcFileHashAndTransferWithAPICall({ getNCFileById, ncFileId: _ncFileId });
  }

  const version = getNCFileFromVersions(selectedItem?.machineStatus?.versions);

  let ncFileContentHash = selectedItem?.machineStatus?.ncFileContentHash || version?.fileContentHash;
  let ncFileTransferFromMachineState = selectedItem?.machineStatus?.ncFileTransferFromMachineState || version?.ncFileTransferFromMachineState;

  const ncFileId = selectedItem?.machineStatus?.ncFileId;

  if (!ncFileContentHash && ncFileId) {
    const { ncFileContentHash: _ncFileContentHash, ncFileTransferFromMachineState: _ncFileTransferFromMachineState } =
      await getNcFileHashAndTransferWithAPICall({ getNCFileById, ncFileId });
    ncFileContentHash = _ncFileContentHash;
    ncFileTransferFromMachineState = _ncFileTransferFromMachineState;
  }

  return { ncFileContentHash, ncFileTransferFromMachineState };
};

export const shareNCFile = async ({
  generateNCFileURL,
  selectedItem,
  getNCFileById,
  isDetailsScreen,
  ncFileData,
  fileName: _fileName,
}: ShareNCFileParams) => {
  const selectedVersion =
    isDetailsScreen && ncFileData ? getNCFileFromVersions(ncFileData?.versions) : await getNCFileHashAndTransfer({ selectedItem, getNCFileById });

  const fileContentHash = selectedVersion?.ncFileContentHash ?? selectedVersion?.fileContentHash;

  const fileUrl = await generateNCFileURL({
    fullFile: true,
    ncFileContentHash: fileContentHash,
  });

  const fileName = _fileName ?? selectedItem?.machineStatus?.ncFileName ?? selectedItem?.machineStatus?.ncFile;

  await downloadFile({ fileUrl, fileName });
};

export const handleNCFileUploadingProcess = ({
  fileData: data,
  alertActionsOnNCFileUploaded,
  onNCFileUploaded,
}: HandleNCFileUploadingProcessParams) => {
  const fileName = data?.name;
  if (!fileName) return;

  Alert({
    title: i18n.t('Processing NC file...'),
    loadingIndicator: true,
    actions: [{ text: i18n.t('Dismiss'), style: 'secondary' }],
  });

  checkEvent('OnProcessedNcFileDocument', {
    timeout: 30000,
    connectionName: 'appHub',
    matcher: (eventData: { ncFile: NCFileSearchItem }) => eventData?.ncFile?.ncFileName === fileName,
  })
    .then((eventData) => {
      const ncFileId = eventData?.ncFile?.ncFileId;
      const ncFileName = eventData?.ncFile?.ncFileName;
      if (!ncFileId) return;

      onNCFileUploaded?.(eventData?.ncFile);

      const alertActions: AlertAction[] = [
        ...injectConditionalArrayItems(alertActionsOnNCFileUploaded, [alertActionsOnNCFileUploaded]),
        { text: i18n.t('Dismiss'), style: 'secondary' },
      ];

      Alert({
        title: i18n.t('NC File uploaded'),
        text: i18n.t('{{fileName}} has been processed successfully', { fileName: ncFileName }),
        actions: alertActions,
      });
    })
    .catch(() => {
      Alert({ title: i18n.t('NC file processing timed out'), type: 'danger' });
    });
};
